/**
 * @file repPricingCards.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The words and figures of the reps' own pricing screen
 *              (RepPricing — Cam, 28 Oct 2026, P7 / P9 / D10: "Cards — one card
 *              per request (customer, product, its orders) with the price box
 *              on the card"). Pure: the server
 *              (services/repPricingService.repLines) decides piles, states and
 *              what a rep may do; this says it on the card.
 *
 *   One price per request by default (Cam: different prices per line "Yes,
 *   but not by default, they should need to split the group first"):
 *   splitEntry turns a request into one card per order line, each priced on
 *   its own.
 *
 *   What a price is per is always said (Cam, D7: "so long as the user is
 *   totally clear what £/ unit UNITS actually are"): unitWordsOf.
 *
 *   1.0.2 (2026-10-28, N6): unitWordsOf reads utils/unitPriceLabel's one rule
 *   (perUnitLabel) — so milk powder of no known weight is "per bag" (it said
 *   the unit was missing) and the words match every other screen.
 */

import { marginOf, parseRepPrice } from './repPricing';
import { money, ukDate } from './pricingCosting';
import { perUnitLabel } from './unitPriceLabel';
import { matchesAllTerms } from './searchMatch';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;

/**
 * What a request's price is per: { unit, per, perWords, known, note }.
 *   unit      "T", "25kg bag", "500kg tote" — the request's own, else from its weight
 *   per       "/T", "/25kg bag"
 *   perWords  "per T", "per 25kg bag"
 *   known     false when the order line has no unit (a price would be a guess)
 */
export function unitWordsOf(entry) {
  const u = perUnitLabel(entry || {});
  if (u.kind === 'none') {
    return { unit: 'each', per: ' each', perWords: 'each', known: true, note: 'No weight on this line — a price for each one' };
  }
  if (!u.known) {
    return {
      unit: '', per: '', perWords: 'per unit', known: false,
      note: 'The order has no unit on this line — ask the office which unit before you price it'
    };
  }
  const unit = u.text.replace(/^\//, '');
  return { unit, per: u.text, perWords: `per ${unit}`, known: true, note: null };
}

/** A price (or a range) per the unit: "£332.00/T", "£330.00–£332.00/T". */
export function priceText(value, per = '') {
  if (value && typeof value === 'object') {
    if (value.value !== null && value.value !== undefined) return `${money(value.value)}${per}`;
    if (value.min !== null && value.min !== undefined) return `${money(value.min)}–${money(value.max)}${per}`;
    return '';
  }
  const n = num(value);
  return n === null ? '' : `${money(n)}${per}`;
}

/** A margin in pounds to the penny (a price may carry 4dp; a margin is money). */
const pounds = (v) => money(Math.round(Number(v) * 100) / 100);

/** "£32.50 (10.9% on cost)" — or '' when there is no margin to give. */
export function marginText(m, per = '') {
  if (!m || m.margin === null || m.margin === undefined) return '';
  const pc = m.margin_pc === null || m.margin_pc === undefined ? '' : ` (${Number(m.margin_pc).toFixed(1)}% on cost)`;
  return `${pounds(m.margin)}${per}${pc}`;
}

/**
 * The margin a typed price makes on the request's cost — a cost range gives
 * a margin range. { margin, margin_pc } | { min, max } | null.
 */
export function liveMargin(rawPrice, cost) {
  const p = parseRepPrice(rawPrice);
  if (p === null || !cost) return null;
  if (cost.value !== null && cost.value !== undefined) {
    const m = marginOf(p, cost.value);
    return m.margin === null ? null : m;
  }
  if (cost.min === null || cost.min === undefined) return null;
  const lo = marginOf(p, cost.max);
  const hi = marginOf(p, cost.min);
  if (lo.margin === null || hi.margin === null) return null;
  return { min: lo, max: hi };
}

/** "£32.50/T (10.9% on cost)" or "£20.00–£32.50/T" for a range. */
export function liveMarginText(m, per = '') {
  if (!m) return '';
  if (m.min) return `${pounds(m.min.margin)}–${pounds(m.max.margin)}${per}`;
  return marginText(m, per);
}

/** Is a margin below nothing? */
export const marginNegative = (m) => !!m && (m.min ? m.min.margin < 0 : m.margin < 0);

/**
 * One earlier sale as the card lists it:
 *   { price: "£329.00/T", when: "delivered 30/09/26 · INV-0101068",
 *     cost: "cost £290.00/T · margin £39.00/T (13.4% on cost)" | '' , notes: [...] }
 */
export function previousSaleWords(p, per = '') {
  const ownPer = p.unit ? `/${p.unit}` : per;
  const how = p.date_from === 'invoice' ? 'invoiced' : p.date_from === 'order' ? 'ordered'
    : (p.method && p.method.kind === 'customer_collects' ? 'collected' : 'delivered');
  const docs = [p.invoice_no, p.order_number].filter(Boolean);
  const when = [p.delivery_date ? `${how} ${ukDate(p.delivery_date)}` : null, docs.length ? docs.join(', ') : null]
    .filter(Boolean).join(' · ');
  const costBits = [];
  if (p.cost !== null && p.cost !== undefined) {
    costBits.push(`cost ${money(p.cost)}${ownPer}`);
    if (p.margin !== null && p.margin !== undefined) {
      costBits.push(`margin ${marginText({ margin: p.margin, margin_pc: p.margin_pc }, ownPer)}`);
    }
  }
  const notes = [];
  if (p.price_from === 'order') notes.push('not invoiced yet — the order\'s price');
  if (p.recipe_match === false) notes.push('different additive recipe');
  if (p.method && p.method.kind && p.method.kind !== 'ours') notes.push(p.method.label);
  return { price: priceText(p.price, ownPer), when, cost: costBits.join(' · '), notes };
}

/**
 * Each order line of a request as its own card (Split): the request's facts,
 * that line's quantity, cost, price, state and what may be done.
 */
export function splitEntry(entry) {
  if (!entry || !Array.isArray(entry.lines) || entry.lines.length < 2) return [entry];
  return entry.lines.map(l => ({
    ...entry,
    key: `${entry.key}#${l.line_id}`,
    split_of: entry.key,
    line_ids: [l.line_id],
    lines: [l],
    quantity: l.quantity,
    supplied: l.supplied,
    tonnes: l.tonnes,
    earliest_delivery: l.delivery_date,
    latest_delivery: l.delivery_date,
    delivered: !!l.delivered,
    sent: l.sent || entry.sent,
    cost: l.cost ? { value: l.cost.value, min: l.cost.value, max: l.cost.value } : null,
    rep_price: l.rep_price ? { value: l.rep_price.price, min: l.rep_price.price, max: l.rep_price.price } : null,
    rep_note: l.rep_price ? l.rep_price.notes || null : null,
    margin: l.margin,
    contract_request: l.contract_request || null,
    can: {
      price: l.can.price,
      pull_back: l.can.pull_back,
      flag: l.can.flag,
      contract: l.can.contract,
      contract_with_price: l.can.contract_with_price,
      withdraw_contract: l.can.withdraw_contract
    }
  }));
}

/** The lines of a request a write may act on (each line's own `can`). */
export const linesThatMay = (entry, what) => (entry.lines || []).filter(l => {
  const c = l.can || {};
  if (what === 'price') return !c.price;
  if (what === 'pull_back') return !!(c.pull_back && c.pull_back.allowed);
  if (what === 'flag') return !c.flag;
  if (what === 'contract') return !c.contract;
  if (what === 'withdraw_contract') return !!l.contract_request;
  return false;
}).map(l => l.line_id);

/**
 * Where the request stands with the office, in words: one sentence when
 * every line says the same, else each line's ("SO-D10433: …; SO-D10603: …").
 */
export function stateWords(entry) {
  const texts = (entry.lines || []).map(l => (l.office ? l.office.text : '')).filter(Boolean);
  const unique = [...new Set(texts)];
  if (unique.length <= 1) return unique[0] || '';
  return (entry.lines || []).map(l => `${l.order_number || 'Order'}: ${l.office ? l.office.text : ''}`).join('; ');
}

/** The state's tone for its chip: ok | wait | warn | inv | flag | todo. */
export function stateTone(entry) {
  if (entry.pile === 'flagged') return 'flag';
  if (entry.pile === 'invoiced') return 'inv';
  const states = new Set((entry.lines || []).map(l => (l.office ? l.office.state : null)));
  if (states.has('pulled_back')) return 'warn';
  if (states.has('held')) return 'warn';
  if (states.has('waiting')) return 'wait';
  if (states.has('confirmed')) return 'ok';
  return 'todo';
}

/** The newest send across these requests: { at, subject, send_id, has_workbook, requests } | null. */
export function latestSend(entries) {
  let best = null;
  let requests = 0;
  for (const e of entries || []) {
    if (!e.sent || !e.sent.at) continue;
    if (!best || new Date(e.sent.at).getTime() > new Date(best.at).getTime()) best = e.sent;
  }
  if (!best) return null;
  for (const e of entries || []) if (e.sent && e.sent.send_id === best.send_id) requests += 1;
  return { ...best, requests };
}

/**
 * The product group's figures on a card, in the request's unit:
 *   { rows: [{ supplier, product, perT, perUnit, range, billed, highest, usual, mine }]
 *       (the members billed this period; with costs not shown, every member),
 *     unbilled: ["County 17 Nuts + Biotin (Mole Valley Feed Solutions)", …],
 *     highest: { supplier, perT, perUnit } | null, usual: "Usually from X (n of m deliveries)",
 *     marginOnHighest(price) → margin | null }
 */
export function groupView(group, unitWords) {
  if (!group) return null;
  const sameAsTonne = unitWords && unitWords.unit === 'T';
  const nameOf = (i) => {
    const name = i.name || i.code || '';
    const add = String(i.additive || '').replace(/^\+\s*/, '').trim();
    // The item's own name usually says its additive already.
    return add && !name.toLowerCase().includes(add.toLowerCase()) ? `${name} + ${add}` : name;
  };
  const all = (group.items || []).filter(i => i.in_pack !== false || i.cost_per_t !== null).map(i => ({
    key: i.item_id,
    supplier: i.supplier || 'No supplier yet',
    product: nameOf(i),
    code: i.code,
    perT: i.cost_per_t,
    perUnit: sameAsTonne ? null : i.cost_per_unit,
    range: i.min_per_t !== null && i.max_per_t !== null && i.min_per_t !== undefined && Math.abs(i.max_per_t - i.min_per_t) > 0.00005
      ? `${money(i.min_per_t)}–${money(i.max_per_t)}/T earlier this period` : '',
    billed: i.bill_date ? `bill ${ukDate(i.counted_on || i.bill_date)}${i.bills > 1 ? ` · ${i.bills} bills` : ''}` : '',
    noBill: i.cost_per_t === null || i.cost_per_t === undefined,
    highest: !!i.highest,
    usual: !!i.usual,
    mine: !!i.mine,
    other_pack: i.in_pack === false ? (i.pack_words || 'another pack') : null
  }));
  // Members with no bill this period are named once, under the costs.
  const rows = group.costs_shown ? all.filter(r => !r.noBill) : all;
  const unbilled = group.costs_shown ? all.filter(r => r.noBill)
    .map(r => (r.supplier && r.supplier !== 'No supplier yet' ? `${r.product} (${r.supplier})` : r.product)) : [];
  const h = group.highest;
  const u = group.usual || {};
  const usual = u.supplier
    ? `Usually from ${u.supplier}${u.deliveries !== null && u.deliveries !== undefined ? ` (${u.deliveries} of ${u.of}` : ` (${u.of}`} ${u.of === 1 ? 'delivery' : 'deliveries'}, last ${u.months || 12} months)`
    : `No deliveries of this group to them in the last ${u.months || 12} months`;
  const others = (u.others || []).filter(o => o.supplier).map(o => `${o.supplier} ${o.deliveries}`).join(', ');
  const highestPer = h ? (sameAsTonne || h.cost_per_unit === null || h.cost_per_unit === undefined ? h.cost_per_t : h.cost_per_unit) : null;
  return {
    name: group.name,
    period: group.period ? group.period.name : null,
    costsShown: !!group.costs_shown,
    rows,
    unbilled,
    highest: h && h.cost_per_t !== null && h.cost_per_t !== undefined ? {
      supplier: h.supplier, perT: h.cost_per_t, perUnit: sameAsTonne ? null : h.cost_per_unit, perCard: highestPer
    } : null,
    usual,
    usualOthers: others,
    /** The margin a price (per the card's unit) makes on the highest cost. */
    marginOnHighest(raw) {
      const p = parseRepPrice(raw);
      if (p === null || highestPer === null || highestPer === undefined) return null;
      // Only per the card's own unit: a tonne card, or a unit the highest was worked into.
      if (!sameAsTonne && (h.cost_per_unit === null || h.cost_per_unit === undefined)) return null;
      const m = marginOf(p, highestPer);
      return m.margin === null ? null : m;
    }
  };
}

/** Does a request match the search box (customer, account, product, code, orders, supplier)? */
export function entryMatches(entry, query) {
  if (!query || !String(query).trim()) return true;
  const fields = [
    entry.customer && entry.customer.name, entry.customer && entry.customer.account_no,
    entry.product && entry.product.name, entry.product && entry.product.code,
    ...(entry.additives || []).map(a => a.name || a.code),
    ...(entry.lines || []).map(l => l.order_number),
    entry.delivery_method && entry.delivery_method.label,
    entry.group && entry.group.name
  ];
  return matchesAllTerms(fields, query);
}

/** Sum of a request's line quantities; its supplied total only when every line has one. */
export function quantitiesOf(entry) {
  const lines = entry.lines || [];
  const qty = round4(lines.reduce((t, l) => t + (num(l.quantity) || 0), 0));
  const all = lines.length > 0 && lines.every(l => num(l.supplied) !== null);
  const some = lines.some(l => num(l.supplied) !== null);
  return {
    quantity: qty,
    supplied: all ? round4(lines.reduce((t, l) => t + num(l.supplied), 0)) : null,
    suppliedState: all ? 'all' : some ? 'some' : 'none'
  };
}

/**
 * A request's open flags, each note once with the orders it is on:
 * [{ ids: [flag ids], note, at, orders: ['SO-D…'] }].
 */
export function flagsOf(entry) {
  const map = new Map();
  for (const l of (entry && entry.lines) || []) {
    const f = l.flag;
    if (!f) continue;
    const k = String(f.note || '').trim();
    if (!map.has(k)) map.set(k, { ids: [], note: f.note, at: f.at, orders: [] });
    const g = map.get(k);
    if (!g.ids.includes(f.id)) g.ids.push(f.id);
    if (l.order_number && !g.orders.includes(l.order_number)) g.orders.push(l.order_number);
  }
  return [...map.values()];
}
